import { useEffect, useState } from 'react';

import { useAppDispatch as useDispatch, useAppSelector as useSelector } from 'dux/app/hooks';

import useSWR from 'swr';

import { breakpoints } from 'Components/ThemeProvider';

import * as UserService from 'Services/UserService';

import getEnv from 'utils/getEnv';
import logSentryError from 'utils/logSentry';
import useMedia from 'utils/useMedia';
import useScript from 'utils/useScript';

import { trackUpdateAddress } from 'dux/tracking/actions';
import generateKustomerLivechatToken from 'dux/auth/generateKustomerLivechatToken';

import { livechatGenerated, livechatOpened } from './actions';
import {
  getHasGeneratedLivechat,
  getUserEmail,
  getUserFirstName,
  getUserLastName,
} from './selectors';
import { fetchAddresses } from './thunks';

export const useFetchAddresses = () => {
  const dispatch = useDispatch();
  const [isDispatchDone, setIsDispatchDone] = useState(false);

  const { data, error, ...ret } = useSWR(
    `useFetchAddresses()`,
    async () => {
      const response = await UserService.fetchAddresses();
      return response;
    },
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (!data && !error) {
      dispatch(fetchAddresses.pending());
      setIsDispatchDone(false);
      return;
    }

    if (error) {
      dispatch(fetchAddresses.rejected(error));
      setIsDispatchDone(true);
      logSentryError(`[dux/user] useFetchAddresses`, error);
      return;
    }

    if (data) {
      dispatch(fetchAddresses.fulfilled(data));
      dispatch(trackUpdateAddress(data));
      setIsDispatchDone(true);
    }
  }, [data, error, dispatch]);

  // Return strategy: Swallow the data to ensure we use the data from redux store (Single source of truth pattern)
  return { isLoading: (!data && !error) || !isDispatchDone, error, ...ret };
};

export const useKustomerLivechat = ({ origin, showLivechat }) => {
  const dispatch = useDispatch();
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
  const hasGeneratedLivechat = useSelector(getHasGeneratedLivechat);
  const userEmail = useSelector(getUserEmail);
  const userFirstname = useSelector(getUserFirstName);
  const userLastname = useSelector(getUserLastName);
  const [initScript, setInitScript] = useState(false);

  useEffect(() => {
    if (
      // Not accesible by Next.js server
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      // Condition: simple feature flag or AB test
      showLivechat &&
      // If already opened, we don't want to generate the chat anymore
      !hasGeneratedLivechat &&
      // We need the user's email to identify the user on Kustomer's dashboard through a token
      userEmail
    ) {
      const token = generateKustomerLivechatToken(userEmail);
      /**
       * Trick for e2e:
       * Delay the Kustomer's script/CDN injection as much as possible, so the event listener can be set before
       * If the script is injected before the listener is listening, the widget is not shown
       */
      setInitScript(true);

      // Show the widget once Kustomer's script has loaded
      window.addEventListener('kustomerLoaded', () => {
        // Generate livechat
        window.Kustomer.start(
          {
            scheduleId: '6179669332a979509ead4cbc',
            ...(isMobile && origin === 'Cart' && { chatIconPosition: { verticalPadding: 120 } }),
          },
          () => {
            // Identify user if token has been successfully generated
            if (token) {
              window.Kustomer.login(
                {
                  jwtToken: token,
                },
                (loginCallbackResponse, err) => {
                  if (loginCallbackResponse) {
                    window.Kustomer.describeCustomer(
                      {
                        customAttributes: {
                          firstNameStr: userFirstname,
                          lastNameStr: userLastname,
                        },
                      },
                      (_callbackResponse, error) => {
                        if (error) {
                          logSentryError(
                            `[${origin} - Kustomer Livechat - Describe customer]`,
                            error
                          );
                        }
                      }
                    );
                  }

                  if (err) {
                    logSentryError(`[${origin} - Kustomer Livechat - Login]`, err);
                  }
                }
              );
            }

            /**
             * Since the hook can be used in several places
             * We don't want to generate the livechat more than once.
             * The opening icon will always be present when triggered the first time.
             */
            dispatch(livechatGenerated());

            // Data tracking: we want to know when the user has clicked on the opening icon
            window.Kustomer.addListener('onOpen', () => dispatch(livechatOpened()));
          }
        );
      });
    }
  }, [showLivechat, userEmail]);

  // Kustomer's script/CDN
  useScript(
    {
      src: 'https://cdn.kustomerapp.com/chat-web/widget.js',
      attribute: {
        name: 'data-kustomer-api-key',
        value: getEnv('REACT_APP_KUSTOMER_LIVECHAT_API_KEY'),
      },
    },
    initScript
  );
};
